import type {
  ChatLinkCandidate,
  Collection,
  PdpQaResolvability,
} from '@/services/products/types'
import { handleApiError } from '../api'
import { BaseService } from '../base/BaseService'

export interface CollectionSelectionOptions {
  currentCollection?: Collection | null
  collections: Collection[]
  preferDefault?: boolean
}

export interface CollectionValidationResult {
  isValid: boolean
  suggestedCollection?: Collection | null
  reason?: string
}

class CollectionService extends BaseService {
  protected readonly baseURL = '/products'

  /**
   * Get all collections for the current organization
   */
  async getCollections(): Promise<{
    success: boolean
    collections: Collection[]
    error?: string
  }> {
    try {
      const response = await this.apiClient.get(`${this.baseURL}/collections`)
      return response.data
    } catch (error) {
      console.error('Failed to get collections:', error)
      return {
        success: false,
        collections: [],
        error:
          error instanceof Error ? error.message : 'Failed to get collections',
      }
    }
  }

  /**
   * List the account's chats with their current feed and product counts
   */
  async getChatLinks(collectionId: string): Promise<ChatLinkCandidate[]> {
    try {
      const response = await this.apiClient.get(
        `${this.baseURL}/collections/${collectionId}/chat_links`
      )

      return response.data.chats
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Point the given chats at this collection
   */
  async linkChats(
    collectionId: string,
    chatConfigurationIds: string[]
  ): Promise<{ linkedCount: number }> {
    try {
      const response = await this.apiClient.post(
        `${this.baseURL}/collections/${collectionId}/chat_links`,
        { chatConfigurationIds }
      )

      return { linkedCount: response.data.linkedCount }
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Find the best collection to select based on preferences
   */
  selectBestCollection(options: CollectionSelectionOptions): Collection | null {
    const { collections, currentCollection, preferDefault = true } = options

    if (collections.length === 0) {
      return null
    }

    // If we have a current collection, validate it still exists
    if (currentCollection) {
      const stillExists = collections.find((c) => c.id === currentCollection.id)
      if (stillExists) {
        return currentCollection
      }
    }

    // Find default collection if preferred
    if (preferDefault) {
      const defaultCollection = collections.find((c) => c.isDefault)
      if (defaultCollection) {
        return defaultCollection
      }
    }

    // Fallback to first collection
    return collections[0] || null
  }

  /**
   * Validate if a collection still exists in the current list
   */
  validateCollection(
    collection: Collection | null,
    availableCollections: Collection[]
  ): CollectionValidationResult {
    if (!collection) {
      return {
        isValid: false,
        reason: 'No collection selected',
      }
    }

    const stillExists = availableCollections.find((c) => c.id === collection.id)
    if (!stillExists) {
      return {
        isValid: false,
        suggestedCollection: this.selectBestCollection({
          collections: availableCollections,
          preferDefault: true,
        }),
        reason: 'Selected collection no longer exists',
      }
    }

    return { isValid: true }
  }

  /**
   * Get collection by ID
   */
  async getCollection(id: string): Promise<{
    success: boolean
    collection?: Collection
  }> {
    try {
      const response = await this.apiClient.get(
        `${this.baseURL}/collections/${id}`
      )
      return { success: true, collection: response.data.collection }
    } catch {
      return { success: false }
    }
  }

  /**
   * Create a new collection
   */
  async createCollection(data: {
    name: string
    slug?: string
    isDefault?: boolean
  }): Promise<{
    success: boolean
    collection?: Collection
    error?: string
  }> {
    try {
      const response = await this.apiClient.post(
        `${this.baseURL}/collections`,
        { collection: data }
      )
      return { success: true, collection: response.data.collection }
    } catch {
      return { success: false, error: 'Failed to create collection' }
    }
  }

  /**
   * Update collection
   */
  async updateCollection(
    id: string,
    data: Partial<Pick<Collection, 'name' | 'slug' | 'isDefault'>>
  ): Promise<{
    success: boolean
    collection?: Collection
    error?: string
  }> {
    try {
      const response = await this.apiClient.put(
        `${this.baseURL}/collections/${id}`,
        { collection: data }
      )
      return { success: true, collection: response.data.collection }
    } catch {
      return { success: false, error: 'Failed to update collection' }
    }
  }

  /**
   * Duplicate a collection along with its products and attribute schemas
   */
  async duplicateCollection(id: string): Promise<{
    success: boolean
    collection?: Collection
    error?: string
  }> {
    try {
      const response = await this.apiClient.post(
        `${this.baseURL}/collections/${id}/duplicate`
      )
      return { success: true, collection: response.data.collection }
    } catch {
      return { success: false, error: 'Failed to duplicate collection' }
    }
  }

  /**
   * Delete collection
   */
  async deleteCollection(id: string): Promise<{
    success: boolean
    error?: string
  }> {
    try {
      await this.apiClient.delete(`${this.baseURL}/collections/${id}`)
      return { success: true }
    } catch {
      return { success: false, error: 'Failed to delete collection' }
    }
  }

  /**
   * Export collection products as CSV and trigger browser download
   */
  async exportCsv(
    collectionId: string,
    collectionName: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.apiClient.get(
        `${this.baseURL}/collections/${collectionId}/export`,
        { responseType: 'blob' }
      )

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const slug = collectionName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
      const filename = `${slug}-export.csv`

      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      return { success: true }
    } catch {
      return { success: false, error: 'Failed to export collection' }
    }
  }

  /**
   * Clear all products from a collection without deleting the collection itself
   */
  async clearProducts(collectionId: string): Promise<{
    success: boolean
    deletedCount?: number
    error?: string
  }> {
    try {
      const response = await this.apiClient.delete(
        `${this.baseURL}/collections/${collectionId}/clear_products`
      )
      return { success: true, deletedCount: response.data.deletedCount }
    } catch {
      return { success: false, error: 'Failed to clear products' }
    }
  }

  /**
   * Get available redirect URL keys for a collection
   */
  async getRedirectKeys(collectionId: string): Promise<{
    success: boolean
    redirectKeys: string[]
    error?: string
  }> {
    try {
      const response = await this.apiClient.get(
        `${this.baseURL}/collections/${collectionId}/redirect_keys`
      )
      return {
        success: true,
        redirectKeys: response.data.redirectKeys || [],
      }
    } catch {
      return {
        success: false,
        redirectKeys: ['redirect_url'],
        error: 'Failed to get redirect keys',
      }
    }
  }

  /**
   * Get PDP Q&A resolvability counts for a collection (or "all" for org-wide)
   */
  async getPdpQaResolvability(collectionId: string): Promise<{
    success: boolean
    resolvability?: PdpQaResolvability
    error?: string
  }> {
    try {
      const response = await this.apiClient.get(
        `${this.baseURL}/collections/${collectionId}/pdp_qa_resolvability`
      )

      return {
        success: true,
        resolvability: response.data.resolvability,
      }
    } catch {
      return {
        success: false,
        error: 'Failed to get PDP Q&A resolvability',
      }
    }
  }

  /**
   * Get collection statistics
   */
  async getCollectionStats(id: string): Promise<{
    success: boolean
    stats?: {
      total_products: number
      active_products: number
      last_import?: string
      last_modified?: string
    }
    error?: string
  }> {
    try {
      const response = await this.apiClient.get(
        `${this.baseURL}/collections/${id}/stats`
      )
      return { success: true, stats: response.data.stats }
    } catch {
      return { success: false, error: 'Failed to get collection stats' }
    }
  }
}

export const collectionService = new CollectionService()
